import { useQuery } from '@tanstack/react-query'
import Processing from 'ext-ui/components/Processing/Processing'
import React, { ReactElement, useEffect, useMemo, useState } from 'react'
import { PlanPeriod } from 'src/app/Onboarding/DynamicPricingTierCard'
import DynamicSaasOnboardingPricingTiers from 'src/app/Onboarding/DynamicSaasOnboardingPricingTiers'
import PricingFeaturesComparisonTable from 'src/app/Onboarding/PricingFeaturesComparisonTable'
import PricingPageHero from 'src/app/Onboarding/PricingPageHero'
import { SupportedBrands } from 'src/enums'
import { CurrencyFormatter } from 'src/localization/currencyFormatter'
import { TranslateFn } from 'src/localization/translate'
import { pricingPlanQueries } from 'src/queries/organizations/pricingPlans/pricingPlans.queries'

import BinCard, { BinCardBankProfile } from './components/BinCard/BinCard'
import style from './MarcomPricingPage.style'

const DEFAULT_MARCOM_BIN = '345000'
const BRAND_ASSETS_BASE_URL = 'https://brand-assets.paywithextend.com'
const PRELOADED_CARD_ART_URLS = [
  `${BRAND_ASSETS_BASE_URL}/amex/CardArt.png`,
  `${BRAND_ASSETS_BASE_URL}/cnb/CardArt.png`,
  `${BRAND_ASSETS_BASE_URL}/hsbc/CardArt.png`,
  `${BRAND_ASSETS_BASE_URL}/tcb/CardArt.png`,
]
const ENTERPRISE_CONTACT_URL = 'https://www.paywithextend.com/support-request'

export interface MarcomPricingPageContentProps {
  currencyFormatter: CurrencyFormatter
  translate: TranslateFn
}

export function MarcomPricingPageContent(
  props: MarcomPricingPageContentProps,
): ReactElement {
  const { currencyFormatter, translate } = props
  const [bin, setBin] = useState('')
  const resolvedBin = bin.length === 6 ? bin : DEFAULT_MARCOM_BIN
  const {
    data: pricingPlans,
    isLoading,
    isPlaceholderData,
    isError,
  } = useQuery(pricingPlanQueries.lookup(resolvedBin))
  const hasLookupError = isError && bin.length === 6

  useEffect(() => {
    PRELOADED_CARD_ART_URLS.forEach((url) => {
      const image = new Image()
      image.src = url
    })
  }, [])

  const sortedPlans = useMemo(() => {
    if (!pricingPlans?.plans) return []
    return [...pricingPlans.plans].sort(
      (a, b) => a.displayOrder - b.displayOrder,
    )
  }, [pricingPlans?.plans])

  const bank = useMemo<BinCardBankProfile | null>(() => {
    if (bin.length !== 6 || isPlaceholderData || !pricingPlans?.plans?.length) {
      return null
    }

    const normalizedPartnerName = pricingPlans.partnerName?.trim()

    if (!normalizedPartnerName) {
      return null
    }

    return {
      cardArt: `${BRAND_ASSETS_BASE_URL}/${normalizedPartnerName.toLowerCase()}/CardArt.png`,
      cardNetwork: normalizedPartnerName.toUpperCase(),
    }
  }, [
    bin.length,
    isPlaceholderData,
    pricingPlans?.partnerName,
    pricingPlans?.plans,
  ])

  const isNonExtendIssuer =
    !!pricingPlans?.partnerName &&
    pricingPlans?.partnerName.toLowerCase() !== SupportedBrands.Extend

  const getPlanCtaAction = ({
    plan,
    planPeriod,
  }: {
    plan: (typeof sortedPlans)[number]
    planPeriod: PlanPeriod
  }) => {
    const href =
      planPeriod === 'YEARLY' ? plan.ctaUrls?.yearly : plan.ctaUrls?.monthly

    return {
      href:
        href ??
        (plan.ctaUrls === null && plan.id.includes('enterprise')
          ? ENTERPRISE_CONTACT_URL
          : undefined),
      rel: 'noopener noreferrer',
      target: '_blank',
    }
  }

  return (
    <div css={style} className="MarcomPricingPage">
      <div className="SaasOnboardingPricingTiers-container flex-column justify-center align-center gap-10">
        {isLoading ? (
          <Processing />
        ) : (
          <>
            <PricingPageHero
              className="MarcomPricingPage-hero"
              translate={translate}
            />
            <div className="MarcomPricingPage-binCard">
              <BinCard
                bank={bank}
                onBinChange={setBin}
                hasError={hasLookupError}
                isLoading={isPlaceholderData}
              />
            </div>
            <DynamicSaasOnboardingPricingTiers
              pricingPlans={sortedPlans}
              showVirtualCardAlert={pricingPlans?.showVCUnavailableAlert}
              isNonExtendIssuer={isNonExtendIssuer}
              currencyFormatter={currencyFormatter}
              translate={translate}
              getPlanCtaAction={getPlanCtaAction}
            />
            <div className="MarcomPricingPage-featuresComparisonTable">
              <PricingFeaturesComparisonTable
                pricingPlans={sortedPlans}
                translate={translate}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}
